import { useCallback, useEffect, useMemo } from "react";
import { useT } from "@sablier/v2-locales";
import { guards } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import {
  useCreateGroupAccessor,
  useCreateGroupField,
  useCreateSingleAccessor,
  useCreateSingleField,
  useModalDurationPicker,
  useModalMomentPicker,
} from "~/client/hooks";
import type { IDynamicUnlockCliff } from "./config";
import { setup } from "../../system/setup";

type IExtension = IDynamicUnlockCliff;

function useExtendedSingleAccess() {
  return useCreateSingleAccessor<IExtension>();
}

function useExtendedGroupAccess() {
  return useCreateGroupAccessor<IExtension>();
}

function useExtendedSingleFields() {
  const extension = useCreateSingleField("extension");
  return extension.field as unknown as IExtension;
}

function useExtendedStream(id: string) {
  const streams = useCreateGroupField("streams");
  const stream = useMemo(
    () => streams.field.value.find((s) => s.id === id),
    [id, streams],
  );

  return stream as typeof stream & { extension: IExtension };
}

export function useSingleFieldEnd() {
  const access = useExtendedSingleAccess();
  const extension = useExtendedSingleFields();
  const linkedEnd = useCreateSingleField("end");
  const linkedDuration = useCreateSingleField("duration");
  const { setOpen } = useModalMomentPicker();
  const { t } = useT();

  const end = useMemo(() => ({ field: extension.end }), [extension]);

  const onConfirm = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      let warning = undefined;

      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateMoment({
          t,
          purpose: "cliff",
          min: state.fields.start.value,
          value,
          max: state.fields.end.value,
        });
      }

      update({
        extension: {
          ...state.fields.extension,
          end: {
            ...state.fields.extension.end,
            value,
            warning,
          },
        },
      });
    },
    [access, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "moment",
        placeKey: "createSingleCliffExponential",
      });
    });
    setOpen(true, {
      milliseconds: end.field.value,
      onConfirm,
      purpose: t("structs.endDate"),
    });
  }, [onConfirm, setOpen, end, t]);

  const placeholder = useMemo(
    () => _.toDuration(new Date().valueOf(), "date")[0],
    [],
  );

  /** React to linked fields: stream end time influences cliff end time */

  useEffect(() => {
    if (!linkedDuration.field.isActive) {
      const state = access();
      onConfirm(state.fields.extension.end.value);
    }
  }, [access, linkedDuration, linkedEnd, onConfirm]);

  return { end, placeholder, onClick };
}

export function useSingleFieldDuration() {
  const access = useExtendedSingleAccess();
  const linkedDuration = useCreateSingleField("duration");
  const extension = useExtendedSingleFields();
  const { setOpen } = useModalDurationPicker();
  const { t } = useT();

  const duration = useMemo(() => ({ field: extension.duration }), [extension]);

  const onConfirm = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;

      let warning = undefined;
      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateDuration({
          t,
          purpose: "cliff",
          max: state.fields.duration.value,
          min: "0",
          value,
        });
      }

      update({
        extension: {
          ...state.fields.extension,
          duration: {
            ...state.fields.extension.duration,
            value,
            warning,
          },
        },
      });
    },
    [access, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) =>
      events.openModalFrom({
        nameKey: "duration",
        placeKey: "createSingleCliffExponential",
      }),
    );
    setOpen(true, {
      milliseconds: duration.field.value,
      onConfirm,
    });
  }, [duration, onConfirm, setOpen]);

  /** React to linked fields: total duration influences cliff duration */

  useEffect(() => {
    if (linkedDuration.field.isActive) {
      const state = access();
      onConfirm(state.fields.extension.duration.value);
    }
  }, [access, linkedDuration, onConfirm]);

  return { duration, onClick };
}

export function useSingleFieldUnlock() {
  const access = useExtendedSingleAccess();
  const extension = useExtendedSingleFields();
  const amount = useCreateSingleField("amount");
  const token = useCreateSingleField("token");
  const { t } = useT();

  const maximum = useMemo(
    () => ({
      label: `${t("words.max")}:`,
      value:
        amount.field.value && amount.field.value !== "0"
          ? _.toNumeralPrice(amount.field.value, false)
          : undefined,
    }),
    [amount, t],
  );

  const unlock = useMemo(() => ({ field: extension.unlock }), [extension]);

  const onBlur = useCallback(() => {
    const state = access();
    const extension = state.fields.extension;
    const value = extension.unlock.value;
    const max = state.fields.amount.value;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(value)) {
      update({
        extension: {
          ...extension,
          unlock: {
            ...extension.unlock,
            warning: undefined,
          },
        },
      });
    } else {
      update({
        extension: {
          ...extension,
          unlock: {
            ...extension.unlock,
            warning: guards.validateAmount({
              t,
              context: "wallet",
              max,
              value,
              min: "0",
            }),
          },
        },
      });
    }
  }, [access, t]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      const extension = state.fields.extension;

      update({
        extension: {
          ...extension,
          unlock: {
            ...extension.unlock,
            value,
            warning: undefined,
          },
        },
      });
    },
    [access],
  );

  const onMax = useCallback(() => {
    const state = access();
    const update = state.api.updateFields;
    const extension = state.fields.extension;
    update({
      extension: {
        ...extension,
        unlock: {
          ...extension.unlock,
          value: state.fields.amount.value,
        },
      },
    });

    onBlur();
  }, [access, onBlur]);

  useEffect(() => {
    onBlur();
  }, [onBlur, amount]);

  return {
    unlock,
    maximum,
    onBlur,
    onChange,
    onMax,
    token,
  };
}

export function useGroupFieldDuration(id: string) {
  const access = useExtendedGroupAccess();
  const stream = useExtendedStream(id);
  const { setOpen } = useModalDurationPicker();
  const { t } = useT();

  const linkedDuration = useMemo(() => stream.duration, [stream]);
  const duration = useMemo(
    () => ({ field: stream.extension.duration }),
    [stream],
  );

  const onConfirm = useCallback(
    (value?: string) => {
      const { index, isValid, stream, streams, update } = setup(access, id);

      if (!isValid) {
        return;
      }

      let warning = undefined;
      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateDuration({
          t,
          purpose: "cliff",
          max: stream.duration.value,
          min: "0",
          value,
        });
      }

      const edited = {
        ...stream,
        extension: {
          ...stream.extension,
          duration: {
            ...stream.extension.duration,
            value,
            warning,
          },
        },
      };

      const newStreams = [...streams];
      newStreams.splice(index, 1, edited);
      update({
        streams: {
          value: newStreams,
        },
      });
    },
    [access, id, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) =>
      events.openModalFrom({
        nameKey: "duration",
        placeKey: "createGroupCliffExponential",
      }),
    );
    setOpen(true, {
      milliseconds: duration.field?.value,
      onConfirm,
    });
  }, [duration, onConfirm, setOpen]);

  /** React to linked fields: total duration influences cliff duration */

  useEffect(() => {
    if (linkedDuration.isActive) {
      const { isValid, stream: item } = setup(access, id);
      if (isValid) {
        onConfirm(item.extension.duration.value);
      }
    }
  }, [access, id, linkedDuration, onConfirm]);

  return { duration, onClick };
}

export function useGroupFieldEnd(id: string) {
  const access = useExtendedGroupAccess();
  const stream = useExtendedStream(id);
  const { setOpen } = useModalMomentPicker();
  const { t } = useT();

  const linkedDuration = useMemo(() => stream.duration, [stream]);
  const linkedEnd = useMemo(() => stream.end, [stream]);

  const end = useMemo(() => ({ field: stream.extension.end }), [stream]);

  const onConfirm = useCallback(
    (value?: string) => {
      const { index, isValid, stream, streams, update } = setup(access, id);

      if (!isValid) {
        return;
      }

      let warning = undefined;
      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateMoment({
          t,
          purpose: "cliff",
          min: stream.start.value,
          value,
          max: stream.end.value,
        });
      }

      const edited = {
        ...stream,
        extension: {
          ...stream.extension!,
          end: {
            ...stream.extension.end,
            value,
            warning,
          },
        },
      };

      const newStreams = [...streams];
      newStreams.splice(index, 1, edited);

      update({
        streams: {
          value: newStreams,
        },
      });
    },
    [access, id, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "moment",
        placeKey: "createGroupCliffExponential",
      });
    });
    setOpen(true, {
      milliseconds: end.field?.value,
      onConfirm,
      purpose: t("structs.endDate"),
    });
  }, [onConfirm, setOpen, end, t]);

  const placeholder = useMemo(
    () => _.toDuration(new Date().valueOf(), "date")[0],
    [],
  );

  /** React to linked fields: stream end date influences cliff end date */

  useEffect(() => {
    if (!linkedDuration.isActive) {
      const { isValid, stream: item } = setup(access, id);
      if (isValid) {
        onConfirm(item.extension.end.value);
      }
    }
  }, [access, id, linkedDuration, linkedEnd, onConfirm]);

  return { end, placeholder, onClick };
}

export function useGroupFieldUnlock(id: string) {
  const access = useExtendedGroupAccess();
  const stream = useExtendedStream(id);
  const token = useCreateGroupField("token");
  const amount = useMemo(() => ({ field: stream.amount }), [stream]);
  const extension = useMemo(() => stream.extension, [stream]);
  const { t } = useT();

  const unlock = useMemo(() => ({ field: stream.extension.unlock }), [stream]);

  const maximum = useMemo(
    () => ({
      label: `${t("words.max")}:`,
      value:
        !_.isNilOrEmptyString(amount.field.value) && amount.field.value !== "0"
          ? _.toNumeralPrice(amount.field.value, false)
          : undefined,
    }),
    [amount, t],
  );

  const onBlur = useCallback(() => {
    const { index, isValid, stream, streams, update } = setup(access, id);
    if (!isValid) {
      return;
    }

    const amount = stream.amount.value;
    const extension = stream.extension;
    const unlock = stream.extension.unlock.value;
    const edited = _.isNilOrEmptyString(unlock)
      ? {
          ...stream,
          extension: {
            ...extension,
            unlock: {
              ...extension.unlock,
              warning: undefined,
            },
          },
        }
      : {
          ...stream,
          extension: {
            ...extension,
            unlock: {
              ...extension.unlock,
              warning: guards.validateAmount({
                t,
                context: "wallet",
                max: amount,
                value: unlock,
                min: "0",
              }),
            },
          },
        };

    const cloned = [...streams];
    cloned.splice(index, 1, edited);

    update({
      streams: {
        value: cloned,
      },
    });
  }, [access, id, t]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const { index, isValid, stream, streams, update } = setup(access, id);

      if (!isValid) {
        return;
      }
      const edited = {
        ...stream,
        extension: {
          ...stream.extension,
          unlock: {
            ...stream.extension.unlock,
            warning: undefined,
            value,
          },
        },
      };

      const newStreams = [...streams];
      newStreams.splice(index, 1, edited);
      update({
        streams: {
          value: newStreams,
        },
      });
    },
    [access, id],
  );

  const onMax = useCallback(() => {
    const { index, isValid, stream, streams, update } = setup(access, id);

    if (!isValid) {
      return;
    }

    const edited = {
      ...stream,
      extension: {
        ...extension,
        unlock: {
          ...extension.unlock,
          value: stream.amount.value,
        },
      },
    };

    const cloned = [...streams];
    cloned.splice(index, 1, edited);

    update({
      streams: {
        value: cloned,
      },
    });

    onBlur();
  }, [access, extension, id, onBlur]);

  useEffect(() => {
    onBlur();
  }, [onBlur]);

  return {
    unlock,
    maximum,
    onBlur,
    onChange,
    onMax,
    token,
  };
}
